import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import {
  Archive,
  CalendarDays,
  Download,
  ExternalLink,
  Eye,
  FileText,
  LoaderCircle,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';

interface ArchiveManifest {
  kind?: string;
  projectName?: string;
  drawingType?: string | null;
  ownerName?: string | null;
  officeName?: string | null;
  totalPages?: number;
  fileCount?: number;
  outputAttachmentId?: string;
  outputName?: string;
  outputSha256?: string;
  verificationUrl?: string;
  coverTemplateKey?: string | null;
  stampPresetKey?: string | null;
}

interface ArchiveRecord {
  id: string;
  titleAr: string;
  storageKey: string | null;
  manifest: ArchiveManifest;
  createdAt: string;
}

export function ResultsPage() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ArchiveRecord | null>(null);
  const archiveQuery = useQuery({
    queryKey: ['archive-records'],
    queryFn: () => api<ArchiveRecord[]>('/api/archive'),
  });

  const records = (archiveQuery.data || []).filter((record) => {
    const haystack = [
      record.titleAr,
      record.manifest.projectName,
      record.manifest.ownerName,
      record.manifest.drawingType,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black text-teal">مخرجات قابلة للتتبع</p>
            <h1 className="mt-2 text-2xl font-black">النتائج والأرشيف</h1>
            <p className="mt-2 max-w-2xl text-xs font-semibold leading-6 text-slate-500">
              ابحث في الحزم السابقة، راجع بياناتها وبصمتها، وعاين ملف PDF قبل التنزيل.
            </p>
          </div>
          <div className="rounded-2xl border border-ink/10 bg-white px-5 py-3 text-center">
            <strong className="block text-2xl text-ink">{archiveQuery.data?.length || 0}</strong>
            <span className="text-[9px] font-bold text-slate-400">سجل مؤرشف</span>
          </div>
        </header>

        <label className="relative mb-5 block max-w-xl">
          <Search className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث باسم المشروع أو المالك أو نوع المخطط…"
            className="w-full rounded-xl border border-ink/10 bg-white px-4 py-3 pe-10 text-xs font-semibold outline-none focus:border-teal focus:ring-4 focus:ring-teal/10"
          />
        </label>

        {archiveQuery.isLoading ? (
          <div className="grid min-h-64 place-items-center">
            <LoaderCircle className="h-7 w-7 animate-spin text-teal" />
          </div>
        ) : records.length > 0 ? (
          <div className="grid gap-4 lg:grid-cols-2">
            {records.map((record) => (
              <article key={record.id} className="rounded-2xl border border-ink/10 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-ink text-white">
                    <Archive className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h2 className="truncate text-sm font-black">
                        {record.manifest.projectName || record.titleAr}
                      </h2>
                      {record.manifest.verificationUrl && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[8px] font-black text-emerald-700">
                          <ShieldCheck className="h-3 w-3" /> موثّق
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-[10px] font-bold text-slate-400">
                      {record.manifest.drawingType || record.titleAr}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-slate-50 p-3 text-center">
                    <FileText className="mx-auto mb-1 h-3.5 w-3.5 text-teal" />
                    <strong className="block text-sm">{record.manifest.fileCount || 0}</strong>
                    <span className="text-[8px] font-bold text-slate-400">ملفات</span>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 text-center">
                    <span className="mx-auto mb-1 block h-3.5 text-[10px] font-black text-teal">#</span>
                    <strong className="block text-sm">{record.manifest.totalPages || 0}</strong>
                    <span className="text-[8px] font-bold text-slate-400">صفحات</span>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3 text-center">
                    <CalendarDays className="mx-auto mb-1 h-3.5 w-3.5 text-teal" />
                    <strong className="block text-[10px]">
                      {new Intl.DateTimeFormat('ar-SA', { dateStyle: 'short' }).format(new Date(record.createdAt))}
                    </strong>
                    <span className="text-[8px] font-bold text-slate-400">تاريخ</span>
                  </div>
                </div>

                {(record.manifest.ownerName || record.manifest.officeName) && (
                  <div className="mt-3 space-y-1 rounded-xl border border-ink/5 p-3 text-[10px] font-semibold text-slate-500">
                    {record.manifest.ownerName && <p>المالك: {record.manifest.ownerName}</p>}
                    {record.manifest.officeName && <p>المكتب: {record.manifest.officeName}</p>}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {record.manifest.outputAttachmentId && (
                    <>
                      <button
                        type="button"
                        onClick={() => setSelected(record)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-3 py-2 text-[10px] font-black text-white hover:bg-teal"
                      >
                        <Eye className="h-3 w-3" /> معاينة PDF
                      </button>
                      <a
                        href={`/api/files/${record.manifest.outputAttachmentId}`}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-600 hover:border-teal/30 hover:text-teal"
                      >
                        <Download className="h-3 w-3" /> تنزيل
                      </a>
                    </>
                  )}
                  {record.manifest.verificationUrl && (
                    <a
                      href={record.manifest.verificationUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-xl border border-gold/20 bg-gold/5 px-3 py-2 text-[10px] font-black text-gold hover:bg-gold/10"
                    >
                      <ExternalLink className="h-3 w-3" /> التحقق
                    </a>
                  )}
                </div>
                {record.manifest.outputSha256 && (
                  <code dir="ltr" className="mt-3 block truncate rounded-lg bg-slate-50 px-2 py-1.5 text-[8px] text-slate-400">
                    {record.manifest.outputSha256}
                  </code>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-ink/15 bg-white/50 px-5 py-20 text-center">
            <Archive className="mx-auto mb-4 h-8 w-8 text-teal" />
            <h2 className="text-base font-black">{query ? 'لا توجد نتائج مطابقة' : 'الأرشيف فارغ'}</h2>
            <p className="mt-1 text-[10px] font-semibold text-slate-500">
              أنشئ حزمة من مركز تجهيز المخططات لتظهر هنا.
            </p>
          </div>
        )}
      </div>

      <Dialog.Root open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/55 backdrop-blur-sm" />
          <Dialog.Content className="fixed inset-x-3 top-3 z-50 flex h-[calc(100vh-1.5rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:inset-x-8 sm:top-6 sm:h-[calc(100vh-3rem)]">
            <div className="flex items-center justify-between border-b border-ink/10 px-4 py-3">
              <div>
                <Dialog.Title className="text-sm font-black">
                  {selected?.manifest.projectName || selected?.titleAr}
                </Dialog.Title>
                <Dialog.Description className="mt-0.5 text-[9px] font-bold text-slate-400">
                  معاينة PDF النهائي
                </Dialog.Description>
              </div>
              <Dialog.Close className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200">
                <X className="h-4 w-4" />
              </Dialog.Close>
            </div>
            {selected?.manifest.outputAttachmentId && (
              <iframe
                title="معاينة PDF"
                src={`/api/files/${selected.manifest.outputAttachmentId}?inline=1`}
                className="h-full w-full flex-1 bg-slate-100"
              />
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
